/**
 * Настоящие даты брони из Квестгида (27.10.2026). Только ЕО. План посчитан офлайн (без телефонов), сервер
 * (routes/kvestgid-origin.ts) сверяет его с живой базой по частям: «готово» / «уже записано» / «изменилось».
 * Брони не меняются: даты ложатся в отдельные таблицы, «Откатить» убирает все загрузки Квестгида одной кнопкой
 * (и прежние, если план пересобрали; плана для отката не нужно).
 * 27.10 (владелец: «добавь как игры без сумм»): третья часть — игры без сумм (Машинарика/Миньоны/Гравити 2019–2021, которых нет в 1С):
 * «Применить» добавляет их бронями на свободные слоты, «Откатить» удаляет (кроме тех, на которые уже есть ссылки). Телефоны —
 * отдельной кнопкой, файлом с компьютера (KvestgidPhonesUpload).
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { ConfirmModal } from "../../components/shared";
import { KvestgidPhonesUpload } from "./KvestgidPhonesUpload";
import css from "./kvestgidOrigin.module.css";

type Counts = Record<string, number>;
interface PartRow { part: number; kind: "origin" | "history" | "games"; from: number; to: number; rows: number; ready: number; applied: number; changed?: number; anchor_changed?: number; slot_busy?: number; other: number }
interface Resp {
  load_id: string | null; generated_at: string | null; part_size: number; plan_error?: string;
  counts: { origin_by_src?: Counts; origin_by_era?: Counts; history_by_why?: Counts };
  parts: PartRow[]; origin: Counts; history: Counts; games?: Counts;
  stored: { origin: number; history: number; anchored: number; origin_all: number; history_all: number; origin_kg: number; history_kg: number;
    games?: number; games_kg?: number; other_kg?: number };
  changed_sample: { id: number; why: "missing" | "moved" }[];
}

const n = (v: number | undefined) => (v ?? 0).toLocaleString("ru-RU");
const day = (d: string | null) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : "");
/** Сколько строк части ещё запишется: даты — готовые; история и игры — и готовые, и те, что лягут без якоря (игры — без клиента). */
const todo = (p: PartRow) => p.ready + (p.kind !== "origin" ? p.anchor_changed ?? 0 : 0);

export function KvestgidOriginPanel() {
  const [data, setData] = useState<Resp | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [ask, setAsk] = useState<"apply" | "rollback" | null>(null);
  const [toast, setToast] = useState("");
  const [open, setOpen] = useState<"parts" | "changed" | null>(null);

  const load = useCallback(() => {
    setErr("");
    api<Resp>("/api/eo/kvestgid-origin").then(setData).catch((e: unknown) => setErr(e instanceof Error ? e.message : "Не удалось загрузить"));
  }, []);
  useEffect(load, [load]);

  const applyAll = async () => {
    const parts = (data?.parts ?? []).filter((p) => todo(p) > 0);
    let written = 0;
    for (const [i, p] of parts.entries()) {
      setBusy(`Записываю часть ${i + 1} из ${parts.length}…`);
      written += (await api<{ inserted: number }>("/api/eo/kvestgid-origin/apply", { method: "POST", body: { part: p.part } })).inserted;
    }
    return `Записано строк: ${n(written)}`;
  };
  const rollback = async () => {
    setBusy("Откатываю…");
    const r = await api<{ origin: number; history: number; games?: number; games_kept?: number }>("/api/eo/kvestgid-origin/rollback", { method: "POST", body: {} });
    const kept = r.games_kept ? `; ${n(r.games_kept)} игр без сумм оставлены — на них уже есть отчёты или правки` : "";
    return `Откачено: ${n(r.origin)} дат брони, ${n(r.history)} строк истории и ${n(r.games)} игр без сумм${kept}`;
  };
  const run = async (kind: "apply" | "rollback") => {
    setAsk(null);
    try { setToast(await (kind === "apply" ? applyAll() : rollback())); } catch (e) { setToast(e instanceof Error ? e.message : "Ошибка"); }
    setBusy(""); load();
  };

  if (err) return <div className={css.card}><div className={css.err}>{err}</div></div>;
  if (!data) return <div className={css.card}><div className={css.muted}>Сверяю даты брони из Квестгида…</div></div>;
  const { origin: o, history: h, stored } = data;
  const g = data.games ?? {};
  const kg = stored.origin_kg + stored.history_kg;
  /* прежняя загрузка — без строк игр без сумм, которые оставил откат (на них есть ссылки): «Применить» они не держат (сервер, OTHER_KG) */
  const otherKg = stored.other_kg ?? Math.max(0, kg - stored.origin - stored.history);
  const ready = otherKg > 0 ? 0 : data.parts.reduce((a, p) => a + todo(p), 0);
  const src = data.counts.origin_by_src ?? {};

  return (
    <div className={css.card}>
      <div className={css.head}>
        <span className={css.title}>Даты брони из Квестгида</span>
        {data.generated_at ? <span className={css.muted}>выгрузка от {day(data.generated_at)}</span> : null}
      </div>
      <p className={css.lead}>
        Настоящие даты брони из Квестгида для старых игр (архив 1С и мартовский импорт) и брони Квестгида, которых в системе нет.
        Сами брони не меняются: даты ложатся в отдельные таблицы, «Откатить» убирает все загрузки Квестгида.
        Игры Квестгида, которых нет в 1С, добавляются новыми бронями без сумм — только на свободные слоты; «Откатить» убирает и их.
      </p>
      {data.plan_error ? <div className={css.err}>План не читается: {data.plan_error}</div> : (
        <>
          <div className={css.sub}>Даты брони — {n(o.rows)} игр (с датой Квестгида {n(src.kvestgid)}, дата неизвестна {n((src.unknown ?? 0) + (src.postfactum ?? 0))})</div>
          <div className={css.totals}>
            <div><b>{n(o.ready)}</b><span>готово к записи</span></div>
            <div><b>{n(o.applied)}</b><span>уже записано</span></div>
            <div><b className={o.changed ? css.warn : undefined}>{n(o.changed)}</b><span>бронь изменилась или удалена — не трогаем</span></div>
            {o.other ? <div><b>{n(o.other)}</b><span>записано другой загрузкой</span></div> : null}
          </div>
          <div className={css.sub}>Брони Квестгида без пары — {n(h.rows)} (якорь — бронь того же клиента в системе)</div>
          <div className={css.totals}>
            <div><b>{n((h.ready ?? 0) + (h.anchor_changed ?? 0))}</b><span>готово к записи{h.anchor_changed ? `, из них ${n(h.anchor_changed)} без якоря` : ""}</span></div>
            <div><b>{n(h.applied)}</b><span>уже записано, с якорем {n(stored.anchored)}</span></div>
            {h.other ? <div><b>{n(h.other)}</b><span>записано другой загрузкой</span></div> : null}
          </div>
          {g.rows ? (
            <>
              <div className={css.sub}>Игры без сумм — {n(g.rows)} (Машинарика, Миньоны, Гравити 2019–2021, которых нет в 1С)</div>
              <div className={css.totals}>
                <div><b>{n((g.ready ?? 0) + (g.anchor_changed ?? 0))}</b><span>добавится игр{g.anchor_changed ? `, из них ${n(g.anchor_changed)} без клиента` : ""}</span></div>
                <div><b>{n(g.applied)}</b><span>уже добавлено</span></div>
                {g.slot_busy ? <div><b>{n(g.slot_busy)}</b><span>слот уже занят — не трогаем</span></div> : null}
                {g.no_quest ? <div><b className={css.warn}>{n(g.no_quest)}</b><span>квеста нет в системе — не добавятся</span></div> : null}
                {g.other ? <div><b>{n(g.other)}</b><span>добавлено другой загрузкой</span></div> : null}
              </div>
            </>
          ) : null}
        </>
      )}
      {otherKg > 0 ? <div className={css.err}>Лежит прежняя загрузка Квестгида ({n(otherKg)} строк): чтобы записать эту, сначала «Откатить», потом «Применить».</div> : null}
      <div className={css.actions}>
        <button type="button" className={css.go} disabled={!!busy || ready === 0} onClick={() => setAsk("apply")}>Применить все готовые ({n(ready)})</button>
        {kg > 0 ? <button type="button" className={css.undo} disabled={!!busy} onClick={() => setAsk("rollback")}>Откатить ({n(kg)})</button> : null}
        {busy ? <span className={css.muted}>{busy}</span> : null}
      </div>
      <KvestgidPhonesUpload onDone={load} />
      {stored.origin_all + stored.history_all > 0 ? <div className={css.muted}>Откат архива 1С не пройдёт, пока здесь есть записи: сначала «Откатить» эту загрузку.</div> : null}
      {data.parts.length ? <button type="button" className={css.toggle} onClick={() => setOpen(open === "parts" ? null : "parts")}>{open === "parts" ? "▾" : "▸"} По частям ({data.parts.length} × до {n(data.part_size)} строк)</button> : null}
      {open === "parts" ? (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead><tr><th>Часть</th><th>Что</th><th>Строк</th><th>Готово</th><th>Записано</th><th>Изменились</th></tr></thead>
            <tbody>{data.parts.map((p) => (
              <tr key={p.part}><td>{p.part + 1}</td><td>{p.kind === "origin" ? "даты брони" : p.kind === "games" ? "игры без сумм" : "без пары"}</td><td>{n(p.rows)}</td>
                <td>{n(todo(p))}</td><td>{n(p.applied)}</td><td>{n(p.kind === "origin" ? p.changed : p.kind === "games" ? p.slot_busy : p.anchor_changed)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
      {data.changed_sample.length ? (
        <>
          <button type="button" className={css.toggle} onClick={() => setOpen(open === "changed" ? null : "changed")}>{open === "changed" ? "▾" : "▸"} Изменились ({n(o.changed)})</button>
          {open === "changed" ? data.changed_sample.map((x) => <div key={x.id} className={css.q}>Бронь №{x.id} — {x.why === "missing" ? "её больше нет" : "создана заново или игра другая после выгрузки"}</div>) : null}
        </>
      ) : null}
      {toast ? <div className={css.toast} role="status">{toast}</div> : null}
      {ask ? (
        <ConfirmModal tone="warn" title={ask === "apply" ? "Записать даты брони из Квестгида?" : "Откатить загрузки Квестгида?"}
          actionLabel={ask === "apply" ? "Записать" : "Откатить"} onAction={() => void run(ask)} onCancel={() => setAsk(null)}>
          {ask === "apply"
            ? `Запишется ${n(ready)} строк частями по ${n(data.part_size)}. Существующие брони, их даты и деньги не меняются; игры без сумм добавятся новыми бронями на свободные слоты. Откатить можно этой же панелью.`
            : `Удалятся ${n(kg)} строк всех загрузок Квестгида и добавленные ими игры без сумм (игры, на которые уже есть отчёты или правки, останутся). Остальные брони не меняются.`}
        </ConfirmModal>
      ) : null}
    </div>
  );
}
